import { createContext, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, FileText, Loader2, Lock, LogOut, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { ROUTES } from "@/lib/routes";
import Logo from "@/components/Logo";
import Background from "@/components/landing/Background";
import { Input } from "@/components/ui/input";
import { OrderGenerator } from "@/components/admin/OrderGenerator";
import { ProformaGenerator } from "@/components/admin/ProformaGenerator";

const StaffCtx = createContext(null);
export const useStaff = () => useContext(StaffCtx);

export function StaffShell({ children, backTo, backLabel }) {
  const [staff, setStaff] = useState(null);
  useSeo({ title: "Dokumenty wewnętrzne — strefa pracownika", description: "Strefa pracownika Twoje Biuro.", path: ROUTES.staff, noindex: true });
  useEffect(() => { api.get("/staff/me").then((r) => setStaff(r.data)).catch(() => setStaff(false)); }, []);
  const logout = async () => { await api.post("/staff/logout").catch(() => {}); setStaff(false); };
  return (
    <StaffCtx.Provider value={staff}>
      <div data-testid="staff-page" className="min-h-screen text-foreground">
        <Background />
        <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-xl">
          <div className="max-w-5xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-3">
            <Link to="/" aria-label="Twoje Biuro — strona główna"><Logo className="h-8 text-slate-900 dark:text-white" showTagline={false} /></Link>
            <div className="flex items-center gap-2">
              {staff && <span data-testid="staff-user" className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground"><ShieldCheck size={13} className="text-brand-teal" /> {staff.name}</span>}
              {backTo && <Link to={backTo} data-testid="staff-back-link" className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-full px-4 py-2 hover:bg-muted transition-colors"><ArrowLeft size={13} /> {backLabel}</Link>}
              {staff && <button data-testid="staff-logout-button" onClick={logout} className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-full px-4 py-2 hover:bg-muted transition-colors"><LogOut size={13} /> Wyloguj</button>}
            </div>
          </div>
        </header>
        <main className="max-w-5xl mx-auto px-4 sm:px-8 py-12 sm:py-20">
          {staff === null ? <Loader2 data-testid="staff-loading" className="animate-spin mx-auto text-slate-500" size={32} /> : staff === false ? <StaffLogin onLogin={setStaff} /> : (<><StaffSecurity />{children}</>)}
        </main>
      </div>
    </StaffCtx.Provider>
  );
}

function StaffLogin({ onLogin }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState("");
  const [code, setCode] = useState("");
  const err = (er) => { const d = er.response?.data?.detail; toast.error(typeof d === "string" ? d : "Nie udało się zalogować."); };
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await api.post("/staff/login", form);
      if (r.data.status === "otp_required") { setChallenge(r.data.challenge_id); setCode(""); }
      else onLogin(r.data);
    } catch (er) { err(er); } finally { setBusy(false); }
  };
  const submitOtp = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const r = await api.post("/staff/login/otp", { challenge_id: challenge, code: code.trim() }); onLogin(r.data); }
    catch (er) { err(er); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-md mx-auto bg-white/85 dark:bg-slate-900/85 backdrop-blur border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-10">
      <div className="w-12 h-12 rounded-2xl bg-brand-navy text-white flex items-center justify-center"><Lock size={20} /></div>
      <h1 data-testid="staff-login-title" className="font-display font-bold tracking-tight text-2xl sm:text-3xl mt-5">Strefa pracownika</h1>
      {!challenge ? (
        <>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">Dokumenty wewnętrzne biura. Zaloguj się kontem pracownika nadanym przez administratora.</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="staff-login-form">
            <div><label className="text-xs font-semibold text-muted-foreground mb-1.5 block">E-mail</label><Input data-testid="staff-login-email" type="email" autoComplete="username" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="bg-background" /></div>
            <div><label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Hasło</label><Input data-testid="staff-login-password" type="password" autoComplete="current-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="bg-background" /></div>
            <button type="submit" data-testid="staff-login-submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-brand-teal hover:bg-brand-tealDark disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-full text-sm transition-colors">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Lock size={15} />} Zaloguj się
            </button>
          </form>
        </>
      ) : (
        <>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">Wpisz 6-cyfrowy kod z aplikacji uwierzytelniającej (2FA).</p>
          <form onSubmit={submitOtp} className="mt-8 space-y-4" data-testid="staff-2fa-form">
            <Input data-testid="staff-2fa-input" autoFocus inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} required className="bg-background text-center tracking-widest" />
            <button type="submit" data-testid="staff-2fa-submit" disabled={busy || code.trim().length !== 6} className="w-full inline-flex items-center justify-center gap-2 bg-brand-teal hover:bg-brand-tealDark disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-full text-sm transition-colors">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Lock size={15} />} Zaloguj się
            </button>
            <button type="button" onClick={() => { setChallenge(""); setCode(""); }} className="w-full text-center text-xs text-muted-foreground hover:text-foreground">Wróć</button>
          </form>
        </>
      )}
    </div>
  );
}

function StaffSecurity() {
  const staff = useStaff();
  const [enabled, setEnabled] = useState(!!staff?.totp_enabled);
  const [mode, setMode] = useState("idle"); // idle | setup | disable
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const err = (e, f) => { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : f); };
  const start = async () => {
    setBusy(true);
    try { const r = await api.post("/staff/2fa/setup/start"); setSetup(r.data); setCode(""); setMode("setup"); }
    catch (e) { err(e, "Nie udało się rozpocząć konfiguracji."); } finally { setBusy(false); }
  };
  const confirm = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/staff/2fa/setup/confirm", { code: code.trim() }); toast.success("2FA włączone."); setEnabled(true); setMode("idle"); setSetup(null); }
    catch (er) { err(er, "Nieprawidłowy kod."); } finally { setBusy(false); }
  };
  const disable = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/staff/2fa/disable", { code: code.trim() }); toast.success("2FA wyłączone."); setEnabled(false); setMode("idle"); setCode(""); }
    catch (er) { err(er, "Nieprawidłowy kod."); } finally { setBusy(false); }
  };
  const secret = setup?.otpauth_uri?.match(/secret=([A-Z2-7]+)/)?.[1] || "";
  return (
    <div data-testid="staff-security" className="max-w-5xl mx-auto mb-8 bg-card border border-border rounded-2xl p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display font-bold text-base flex items-center gap-2"><ShieldCheck size={16} className="text-brand-teal" /> Bezpieczeństwo — logowanie dwuetapowe (2FA)</h3>
          <p className="text-xs text-muted-foreground mt-1">{enabled ? "2FA jest WŁĄCZONE dla Twojego konta." : "Zwiększ bezpieczeństwo kodem z aplikacji Google Authenticator (opcjonalne)."}</p>
        </div>
        {mode === "idle" && !enabled && <button data-testid="staff-2fa-enable" onClick={start} disabled={busy} className="text-xs font-semibold bg-brand-teal hover:bg-brand-tealDark text-white rounded-full px-4 py-2 transition-colors">Włącz 2FA</button>}
        {mode === "idle" && enabled && <button data-testid="staff-2fa-disable-start" onClick={() => { setCode(""); setMode("disable"); }} className="text-xs font-semibold border border-brand-red/40 text-brand-red rounded-full px-4 py-2 hover:bg-brand-red/10">Wyłącz 2FA</button>}
      </div>
      {mode === "setup" && setup && (
        <form onSubmit={confirm} className="mt-5 flex flex-col sm:flex-row gap-5 items-start" data-testid="staff-2fa-setup">
          <img src={setup.qr_data_uri} alt="Kod QR 2FA" className="w-40 h-40 rounded-lg border border-border bg-white p-2" />
          <div className="flex-1">
            <p className="text-xs text-muted-foreground">Zeskanuj kod QR w aplikacji Google Authenticator, albo wpisz klucz ręcznie:</p>
            <code className="block text-center text-xs font-mono bg-muted rounded-md py-1.5 px-2 my-2 break-all">{secret}</code>
            <Input data-testid="staff-2fa-setup-input" inputMode="numeric" maxLength={6} placeholder="6-cyfrowy kod" value={code} onChange={(e) => setCode(e.target.value)} className="bg-background text-center tracking-widest" />
            <div className="flex gap-2 mt-3">
              <button type="submit" data-testid="staff-2fa-setup-confirm" disabled={busy || code.trim().length !== 6} className="text-xs font-semibold bg-brand-teal hover:bg-brand-tealDark text-white rounded-full px-4 py-2 disabled:opacity-60">Potwierdź i włącz</button>
              <button type="button" onClick={() => { setMode("idle"); setSetup(null); }} className="text-xs font-semibold border border-border rounded-full px-4 py-2 hover:bg-muted">Anuluj</button>
            </div>
          </div>
        </form>
      )}
      {mode === "disable" && (
        <form onSubmit={disable} className="mt-5 flex flex-wrap items-end gap-3" data-testid="staff-2fa-disable">
          <div><label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Podaj aktualny kod z aplikacji, aby wyłączyć</label><Input data-testid="staff-2fa-disable-input" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} className="bg-background text-center tracking-widest w-40" /></div>
          <button type="submit" data-testid="staff-2fa-disable-confirm" disabled={busy || code.trim().length !== 6} className="text-xs font-semibold border border-brand-red/40 text-brand-red rounded-full px-4 py-2 hover:bg-brand-red/10 disabled:opacity-60">Wyłącz 2FA</button>
          <button type="button" onClick={() => setMode("idle")} className="text-xs font-semibold border border-border rounded-full px-4 py-2 hover:bg-muted">Anuluj</button>
        </form>
      )}
    </div>
  );
}

function StaffList() {
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/staff/documents").then((r) => setList(r.data.documents)).catch(() => setList([])); }, []);
  return (
    <>
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground font-semibold"><span aria-hidden="true" className="inline-block w-6 h-[3px] rounded-full bg-brand-navy dark:bg-slate-300 mr-3 align-middle" />Strefa pracownika</p>
      <h1 data-testid="staff-docs-title" className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-4">Dokumenty wewnętrzne</h1>
      <p className="mt-4 max-w-2xl text-sm sm:text-base text-muted-foreground leading-relaxed">Wzory dostępne tylko dla zespołu biura. Wypełnij dane (NIP pobiera dane z GUS) i pobierz gotowy PDF.</p>
      {!list && <Loader2 className="animate-spin mt-10 text-slate-500" />}
      <div data-testid="staff-docs-list" className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
        {(list || []).map((d) => (
          <Link key={d.id} to={ROUTES.staffDoc(d.id)} data-testid={`staff-doc-${d.id}`} className="group bg-white/85 dark:bg-slate-900/85 backdrop-blur border border-slate-200 dark:border-slate-800 rounded-2xl p-5 hover:-translate-y-0.5 hover:shadow-lg hover:border-brand-teal/50 transition-all">
            <div className="flex items-start gap-4">
              <span className="w-10 h-10 rounded-xl bg-brand-navy text-white flex items-center justify-center shrink-0"><FileText size={18} /></span>
              <div className="flex-1">
                <h2 className="font-display font-bold text-base sm:text-lg tracking-tight group-hover:text-brand-teal transition-colors">{d.name}</h2>
                {d.description && <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{d.description}</p>}
                <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mt-3">{d.fields.length} pól do wypełnienia</p>
              </div>
              <ArrowUpRight size={16} className="text-muted-foreground group-hover:text-brand-teal" />
            </div>
          </Link>
        ))}
      </div>
      {list && !list.length && <p data-testid="staff-docs-empty" className="mt-10 text-sm text-muted-foreground">Brak dokumentów wewnętrznych. Administrator może oznaczyć dokument jako „wewnętrzny” w panelu → Generator dokumentów.</p>}
    </>
  );
}

export default function StaffDocuments() {
  return (
    <StaffShell>
      <div className="space-y-10">
        <OrderGenerator context="staff" />
        <ProformaGenerator context="staff" />
        <StaffList />
      </div>
    </StaffShell>
  );
}
